import { supabase } from "@/integrations/supabase/client";
import {
  buildInterpreterSessionRow,
  type InterpreterTurn,
} from "@/lib/interpreter";

export type PersistInterpreterResult = {
  sessionId: string | null;
  situationId: string | null;
  matchedSlug: string;
  matchedTitle: string;
};

const EMPTY = (matchedSlug: string, matchedTitle: string): PersistInterpreterResult => ({
  sessionId: null,
  situationId: null,
  matchedSlug,
  matchedTitle,
});

export async function persistInterpreterSession(input: {
  startedAt: number;
  transcripts: InterpreterTurn[];
}): Promise<PersistInterpreterResult> {
  const { data: { user } } = await supabase.auth.getUser();
  const built = buildInterpreterSessionRow({
    userId: user?.id ?? "anon",
    startedAt: input.startedAt,
    transcripts: input.transcripts,
  });

  if (!user) return EMPTY(built.matched.slug, built.matched.title);

  let situationId: string | null = null;
  const created = await supabase
    .from("situations")
    .insert({
      user_id: user.id,
      title: built.situationTitle,
      description: built.situationDescription,
      status: "ready",
    })
    .select("id")
    .single();
  situationId = created.data?.id ?? null;

  const row = {
    ...built.row,
    user_id: user.id,
    situation_id: situationId,
  };

  let session = (await supabase
    .from("conversation_sessions")
    .insert(row)
    .select("id")
    .single()).data;

  if (!session) {
    const fallback = await supabase
      .from("conversation_sessions")
      .insert({
        user_id: user.id,
        situation_id: situationId,
        started_at: row.started_at,
        ended_at: row.ended_at,
        duration_seconds: row.duration_seconds,
        transcript: row.transcript,
      })
      .select("id")
      .single();
    session = fallback.data;
    if (fallback.error) {
      console.error("Failed to persist interpreter session", fallback.error);
    }
  }

  const { error: settleError } = await supabase.rpc("settle_voice_minutes", {
    minutes: built.billedMinutes,
  });
  if (settleError) {
    console.error("Failed to settle interpreter minutes", settleError);
  }

  return {
    sessionId: session?.id ?? null,
    situationId,
    matchedSlug: built.matched.slug,
    matchedTitle: built.matched.title,
  };
}
